import { useEffect, useState } from 'react'
import { InlineAddChip } from 'app/components/shared/InlineAddActionLink'
import { EDITABLE_CELL_HOVER } from 'app/components/table/cellInteraction'
import ComboAsync from 'app/components/ui/combo/combo-async'
import NameIcon from '../shared/NameIcon'
import type { StaffOptionValue, UserJson } from 'app/types/entities'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { usersFetcher } from 'app/lib/fetchers/usersFetcher'
import { bg } from 'lib/background'
import { lastNameInitial } from 'lib/nameUtils'
import { run } from 'lib/run'

type Props = {
  user?: StaffOptionValue
  contactId?: string
  locationId?: string
  workspaceId?: string
  firstNameInitial?: boolean
  fetcher?: (params: {
    query: string
    [key: string]: string | undefined
  }) => Promise<{ hits: { id: string; name: string }[]; total: number; page: number }>
  onChange: (val?: { id: string; name: string; value: StaffOptionValue }) => void
}

/** Validation cache — keyed by workspaceId:userId */
let cache: Record<string, StaffOptionValue | null> = {}
let fetching: Record<string, Promise<StaffOptionValue | null>> = {}

/** Draft cache — keyed by contactId, survives virtualization unmount/remount */
const draftCache = new Map<string, StaffOptionValue | undefined>()

/** Clear both caches. Exported for testing. */
export const clearUserAssignCache = () => {
  cache = {}
  fetching = {}
  draftCache.clear()
}

export async function getValidatedUser(
  leadOwner: StaffOptionValue | undefined,
  workspaceId: string | undefined,
): Promise<StaffOptionValue | null> {
  if (!leadOwner?.id || leadOwner.id === `AI` || !workspaceId) return null
  const key = `${workspaceId}:${leadOwner.id}`
  if (cache[key] !== undefined) return cache[key]
  if (fetching[key] != null) return fetching[key]
  return (fetching[key] = run(async () => {
    const { hits } = await gqlFetchList<{ id: string; name: string }>(
      `users`,
      {
        search: `id:${leadOwner.id}`,
        pagination: { limit: 1 },
      },
      `id name`,
    )
    const match = hits.find((u) => u.id === leadOwner.id)
    return (cache[key] = match ? { id: match.id, name: match.name } : null)
  }))
}

export function UserAssignCell({
  user,
  contactId,
  locationId,
  workspaceId,
  firstNameInitial,
  fetcher,
  onChange,
}: Props) {
  const [currentUser, setCurrentUser] = useState<StaffOptionValue | undefined>(() => {
    if (contactId && draftCache.has(contactId)) return draftCache.get(contactId)
    if (!user?.id || user.id === `AI` || !workspaceId) return undefined
    return cache[`${workspaceId}:${user.id}`] ?? undefined
  })

  useEffect(() => {
    if (contactId && draftCache.has(contactId)) return
    if (!user?.id) return
    bg(
      getValidatedUser(user, workspaceId).then((resolved) => {
        if (contactId && draftCache.has(contactId)) return
        setCurrentUser(resolved ?? undefined)
      }),
    )
  }, [user?.id])

  // When server data catches up with draft, clear draft
  useEffect(() => {
    if (!contactId || !draftCache.has(contactId)) return
    const draft = draftCache.get(contactId)
    if (draft?.id === user?.id && draft?.name === user?.name) {
      draftCache.delete(contactId)
    }
  }, [user?.id, user?.name])

  const getNameInitials = () => {
    return firstNameInitial
      ? currentUser?.name?.[0]
      : `${currentUser?.name?.[0]}${lastNameInitial(currentUser?.name ?? ` `)?.toLocaleUpperCase() ?? ``}`
  }

  const borderClassName = `border border-strong`
  const backgroundClassName = `bg-surface-strong`
  const textClassName = `text-strong font-bold`

  return (
    <span className="w-full">
      <ComboAsync
        // ComboAsync hangs the tint's `relative h-full` wrapper off this class,
        // so it is the one element that spans the whole cell, which also makes it
        // the right place to clip the "+ Assign" chip when the verb expands wider
        // than the column. The options list is a portaled Radix popover, so it is
        // unaffected.
        className={`min-w-0 overflow-hidden px-2 ${EDITABLE_CELL_HOVER}`}
        placeholder=""
        inputClassName="h-full! border-0! shadow-none! py-0! pl-0! pr-5! cursor-pointer"
        chevronClassName="hidden group-hover/cell:block [&_.chevron-icon]:h-3 [&_.chevron-icon]:w-3 [&_.chevron-icon]:text-minimal"
        showSearchInDropdown={true}
        customIcon={
          currentUser?.name ? (
            <NameIcon
              fullName={getNameInitials()}
              heightClassName={`h-5`}
              widthClassName={`w-5`}
              borderClassName={borderClassName}
              backgroundClassName={backgroundClassName}
              textClassName={textClassName}
              isTiny
            />
          ) : (
            <InlineAddChip verb="Assign" />
          )
        }
        value={
          currentUser
            ? {
                name: currentUser?.name,
                id: currentUser?.id ?? ``,
                value: currentUser?.id ?? ``,
              }
            : { name: ` `, id: ``, value: `` }
        }
        optionsMapper={(res) => {
          return res?.hits?.map((hit: UserJson) => ({
            id: hit.id,
            name: hit.name,
            value: { id: hit.id!, name: hit.name! },
            meta: {
              nameInitial: true,
              borderClassName,
              backgroundClassName,
              textClassName,
            },
          }))
        }}
        queryURL={undefined}
        fetcher={fetcher ?? ((params) => usersFetcher({ ...params, workspaceId: workspaceId! }))}
        additionalParams={{
          locationId: locationId!,
          workspaceId: workspaceId!,
        }}
        onChange={(val) => {
          if (currentUser?.id === val?.value?.id || currentUser?.name === val?.value?.name) {
            setCurrentUser(undefined)
            if (contactId) draftCache.set(contactId, undefined)
            onChange({ id: `-1`, name: ``, value: { id: `-1`, name: `` } })
          } else {
            const selected = { id: val?.value.id, name: val?.value.name }
            if (contactId) draftCache.set(contactId, selected)
            setCurrentUser(selected)
            onChange(val)
          }
        }}
        allowDeselectOption
      />
    </span>
  )
}
